import { useQuery } from '@tanstack/react-query';
import type { HealthChecksView } from '@lp/contracts';
import { errorText, get } from '@/lib/api';
import { dateTime } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { Badge, Card, ErrorBox, Kpi, Loading, PageHeader } from '@/components/ui';

interface Health {
  status: 'ok' | 'degraded';
  db: 'ok' | 'error';
  dbLatencyMs: number;
  environment: string | null;
  now: string;
}

/** is the platform itself well — grows with each phase (jobs, backups, result sources) */
export function HealthPage() {
  const t = useT();
  const h = useQuery({ queryKey: ['health', 'page'], queryFn: () => get<Health>('/health'), refetchInterval: 15_000 });
  const checks = useQuery({ queryKey: ['health', 'checks'], queryFn: () => get<HealthChecksView>('/platform/health/checks'), refetchInterval: 60_000 });
  return (
    <>
      <PageHeader title={t('System Health')} subtitle={t('Checked every 15 seconds.')} />
      {h.isLoading ? (
        <Loading />
      ) : h.error ? (
        <ErrorBox text={errorText(h.error)} />
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Kpi label={t('Platform')} value={<Badge tone={h.data!.status === 'ok' ? 'green' : 'red'}>{h.data!.status === 'ok' ? t('Working') : t('Problem')}</Badge>} />
            <Kpi label={t('Database')} value={<Badge tone={h.data!.db === 'ok' ? 'green' : 'red'}>{h.data!.db === 'ok' ? t('Working') : t('Problem')}</Badge>} sub={`${h.data!.dbLatencyMs} ms`} />
            <Kpi label={t('Checked at')} value={<span className="text-base">{dateTime(h.data!.now)}</span>} />
          </div>
          <Card title={t('Checks')}>
            {checks.isLoading ? (
              <Loading />
            ) : checks.error || !checks.data ? (
              <ErrorBox text={errorText(checks.error)} />
            ) : (
              <ul className="divide-y divide-ink-100" data-testid="checks">
                {checks.data.checks.map((c) => (
                  <li key={c.key} className="py-2 text-sm" data-testid={`check-${c.key}`}>
                    <div className="flex items-center gap-2">
                      <Badge tone={c.severity === 'ok' ? 'green' : c.severity === 'warning' ? 'amber' : 'red'}>{t(c.severity === 'ok' ? 'Working' : c.severity === 'warning' ? 'Warning' : 'Problem')}</Badge>
                      <span className="font-medium">{t(c.label)}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-ink-600">{c.detail}</p>
                    {c.items.length > 0 && (
                      <ul className="mt-1 list-inside list-disc text-xs text-ink-500">
                        {c.items.map((i) => (
                          <li key={i} className="font-mono">
                            {i}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </>
  );
}
